import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import mascotImg from '../assets/mascot-chat.png';

function buildPageContext(pathname, companyProfile, strategyInstances) {
  const page = pathname.split('/')[1] || 'dashboard';
  const ctx = { current_page: '/' + page };

  // Profil entreprise (toujours inclus)
  if (companyProfile) {
    ctx.company = {
      name: companyProfile.name || '',
      target_audience: companyProfile.target_audience || '',
      services: companyProfile.services || '',
      unique_selling_point: companyProfile.unique_selling_point || '',
      communication_tone: companyProfile.communication_tone || '',
      price_range: companyProfile.price_range || '',
      sector: companyProfile.sector || '',
    };
    // Audit
    if (companyProfile.marketing_audit?.overall_score != null) {
      ctx.audit = {
        score: companyProfile.marketing_audit.overall_score,
        top_priority: companyProfile.marketing_audit.top_priority || '',
        audited_at: companyProfile.marketing_audit.audited_at || '',
        sections_summary: (companyProfile.marketing_audit.sections || []).map(s => `${s.title}:${s.score}(${s.status})`).join(', '),
      };
    }
    // Réseaux sociaux
    const social = companyProfile.social_analysis || {};
    const nets = Object.entries(social).map(([net, data]) => `${net}:${data.score ?? '?'}/100`);
    if (nets.length > 0) ctx.social_scores = nets.join(', ');
    // GMB
    if (companyProfile.gmb_has_page != null) {
      ctx.gmb_has_page = companyProfile.gmb_has_page;
    }
  }

  // Stratégies actives
  if (strategyInstances && strategyInstances.length > 0) {
    ctx.active_strategies = strategyInstances.map(s => {
      const total = (s.phases || []).reduce((sum, p) => sum + p.actions.length, 0);
      const done  = (s.phases || []).reduce((sum, p) => sum + p.actions.filter(a => a.done).length, 0);
      const pct   = total > 0 ? Math.round(done / total * 100) : 0;
      return `"${s.title}" (${pct}% complétée)`;
    }).join(', ');
  }

  return ctx;
}

function calcProfileScore(cp) {
  if (!cp) return 0;
  let s = 0;
  if (cp.name) s += 10; if (cp.target_audience) s += 12; if (cp.services) s += 12;
  if (cp.unique_selling_point) s += 10; if (cp.communication_tone) s += 8; if (cp.price_range) s += 5;
  if (cp.logo_url) s += 8; if (cp.facebook_url) s += 5; if (cp.instagram_url) s += 5;
  if (cp.linkedin_url) s += 4; if (cp.google_mybusiness_url || cp.gmb_has_page) s += 8;
  if (cp.marketing_audit?.overall_score != null) s += 13;
  return Math.min(100, s);
}

function getProfileCompletionSuggestions(cp) {
  if (!cp) return [];
  const missing = [];
  if (!cp.logo_url || !cp.communication_tone) missing.push({ icon: '🖼️', text: "Aide-moi à compléter mon identité visuelle" });
  if (!cp.target_audience || !cp.services || !cp.unique_selling_point) missing.push({ icon: '✍️', text: "Aide-moi à décrire mon positionnement" });
  if (!cp.facebook_url && !cp.instagram_url && !cp.linkedin_url) missing.push({ icon: '📱', text: "Quels réseaux sociaux me conseilles-tu ?" });
  if (!cp.marketing_audit) missing.push({ icon: '📊', text: "Lance mon audit marketing" });
  return missing.slice(0, 2);
}

function getPageSuggestions(pathname, companyProfile) {
  const page = pathname.split('/')[1] || 'dashboard';
  const score = calcProfileScore(companyProfile);
  const base = [
    { icon: '🎯', text: "Que faire en priorité aujourd'hui ?" },
    { icon: '💡', text: "Comment améliorer mon score marketing ?" },
  ];
  const byPage = {
    contacts:   [{ icon: '🔍', text: "Quels contacts devrais-je relancer ?" }, { icon: '📊', text: "Analyse ma base de contacts" }],
    campaigns:  [{ icon: '📧', text: "Aide-moi à rédiger une campagne" }, { icon: '🚀', text: "Quelle campagne lancer maintenant ?" }],
    strategy:   [{ icon: '🗺️', text: "Quelle stratégie me conseilles-tu ?" }, { icon: '📈', text: "Comment accélérer ma progression ?" }],
    birthdays:  [{ icon: '🎂', text: "Conseils pour mes emails d'anniversaire ?" }, { icon: '💌', text: "Comment personnaliser mes messages ?" }],
    analytics:  [{ icon: '📉', text: "Pourquoi mon taux d'ouverture baisse ?" }, { icon: '🔎', text: "Analyse mes résultats récents" }],
    settings:   [{ icon: '🏢', text: "Comment améliorer mon profil entreprise ?" }, { icon: '⚙️', text: "Que configurer en priorité ?" }],
    dashboard:  [{ icon: '⚡', text: "Que faire en priorité aujourd'hui ?" }, { icon: '👥', text: "Mes clients dormants, comment les réactiver ?" }],
  };
  const pageSuggestions = byPage[page] || byPage.dashboard;

  // Sous 80% : injecter des suggestions de complétion en tête
  if (score < 80 && companyProfile) {
    const completionSuggestions = getProfileCompletionSuggestions(companyProfile);
    return [...completionSuggestions, ...pageSuggestions, ...base].slice(0, 4);
  }

  return [...pageSuggestions, ...base].slice(0, 4);
}

const STORAGE_KEY = 'appee_chat_v1';

export default function ChatBubble() {
  const { user, isAuthenticated, tenant, companyProfile, strategyInstances } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const aiUsed      = tenant?.ai_used  ?? 0;
  const aiLimit     = tenant?.ai_limit ?? 50;
  const hasCredits  = aiLimit - aiUsed > 0;

  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew]   = useState(true);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const panelRef       = useRef(null);

  // Restore history from localStorage
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (Array.isArray(saved) && saved.length > 0) setMessages(saved);
    } catch {}
  }, []);

  // Persist history
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
  }, [messages]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (open) setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [messages, open, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 280);
      setHasNew(false);
    }
  }, [open]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = [...messages, userMsg].slice(-10).map(m => ({
        role: m.role, content: m.content,
      }));
      const pageCtx = buildPageContext(location.pathname, companyProfile, strategyInstances);
      const res = await api.chatMessage(msg, history, pageCtx);
      setMessages(prev => [...prev, {
        role: 'appee',
        content: res.reply,
        link: res.link || null,
        link_label: res.link_label || null,
        ts: Date.now(),
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'appee',
        content: "Oups, je suis momentanément indispo. Réessaie dans un instant ! 🙏",
        ts: Date.now(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, location, companyProfile, strategyInstances]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearHistory = () => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  if (!isAuthenticated) return null;

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <>
      {/* ── Chat panel ───────────────────────────────────────────── */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed', bottom: 88, right: 20, zIndex: 1000,
          width: 390, maxWidth: 'calc(100vw - 40px)',
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 24px 64px rgba(90,53,255,0.18), 0 4px 24px rgba(0,0,0,0.09)',
          display: 'flex', flexDirection: 'column',
          transform: open ? 'scale(1) translateY(0)' : 'scale(0.88) translateY(16px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'all' : 'none',
          transition: 'all 0.28s cubic-bezier(0.34, 1.46, 0.64, 1)',
          transformOrigin: 'bottom right',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #5A35FF 0%, #7C3AED 100%)',
          padding: '14px 18px',
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          <div style={{
            width: 50, height: 50, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6428F4, #C93DB0, #FF7A1A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, padding: 3, boxSizing: 'border-box',
          }}>
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <img src={mascotImg} alt="Appee" style={{ width: 38, height: 38, objectFit: 'contain' }} />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>Appee</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', display: 'inline-block', flexShrink: 0 }} />
              Le génie de tes relations
            </div>
            {location.pathname !== '/' && (
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, marginTop: 1 }}>
                📍 Contexte : {({
                  'contacts': 'Contacts',
                  'campaigns': 'Campagnes',
                  'strategy': 'Stratégies',
                  'birthdays': 'Anniversaires',
                  'analytics': 'Analytics',
                  'settings': 'Paramètres',
                  'dashboard': 'Dashboard',
                  'imports': 'Imports',
                })[location.pathname.split('/')[1]] || location.pathname}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {messages.length > 0 && (
              <button
                onClick={clearHistory}
                title="Effacer la conversation"
                style={{
                  background: 'rgba(255,255,255,0.12)', border: 'none', color: 'rgba(255,255,255,0.7)',
                  width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
              >🗑</button>
            )}
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
                width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >✕</button>
          </div>
        </div>

        {/* Messages area */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '16px',
          display: 'flex', flexDirection: 'column', gap: 14,
          minHeight: 320, maxHeight: 400,
          scrollbarWidth: 'thin',
          scrollbarColor: '#E5E7EB transparent',
        }}>
          {!hasCredits ? (
            /* No-credit screen */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '24px 8px', textAlign: 'center' }}>
              <img src={mascotImg} alt="Appee" style={{ width: 72, height: 72, objectFit: 'contain', opacity: 0.7 }} />
              <div>
                <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: '#1F2937' }}>
                  Tu n'as plus de crédits IA 😔
                </p>
                <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                  Tes crédits Appee sont épuisés. Recharge pour continuer à bénéficier des conseils personnalisés, des analyses et du génie IA.
                </p>
              </div>
              <button
                onClick={() => { navigate('/settings?tab=plan'); setOpen(false); }}
                style={{
                  background: 'linear-gradient(135deg, #6428F4, #C93DB0, #FF7A1A)',
                  color: '#fff', border: 'none', borderRadius: 12,
                  padding: '12px 24px', fontSize: 14, fontWeight: 800,
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 4px 16px rgba(100,40,244,0.35)',
                  transition: 'opacity .15s, transform .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}
              >
                ✨ Acheter des crédits IA
              </button>
              <p style={{ margin: 0, fontSize: 11, color: '#9CA3AF' }}>
                Accès immédiat après achat
              </p>
            </div>
          ) : messages.length === 0 ? (
            /* Welcome screen */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <AppeeMessage>
                <p style={{ margin: 0, fontSize: 14, color: '#1F2937', lineHeight: 1.6 }}>
                  {firstName ? `Salut ${firstName} ! 👋` : 'Salut ! 👋'} Moi c'est Appee, ton expert marketing dans ta poche. Pose-moi n'importe quelle question — stratégie, clients, campagnes, contenu… je suis là pour te donner le meilleur conseil !
                </p>
              </AppeeMessage>
              <div style={{ paddingLeft: 38 }}>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>SUGGESTIONS</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {getPageSuggestions(location.pathname, companyProfile).map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s.text)}
                      style={{
                        background: '#F8F7FF', border: '1.5px solid #EDE9FF',
                        borderRadius: 11, padding: '9px 13px',
                        cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#5A35FF',
                        display: 'flex', alignItems: 'center', gap: 9,
                        transition: 'all 0.15s', fontFamily: 'inherit',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#EDE9FF'; e.currentTarget.style.borderColor = '#C4B5FD'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#F8F7FF'; e.currentTarget.style.borderColor = '#EDE9FF'; }}
                    >
                      <span style={{ fontSize: 16 }}>{s.icon}</span>
                      <span>{s.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Message history */
            messages.map((m, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', gap: 9,
                  flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                }}
              >
                {m.role === 'appee' && <BeeAvatar />}
                <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: 6, alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    background: m.role === 'user' ? 'linear-gradient(135deg, #5A35FF, #7C3AED)' : '#F3F4F6',
                    color: m.role === 'user' ? '#fff' : '#1F2937',
                    borderRadius: m.role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
                    padding: '10px 14px',
                    fontSize: 14,
                    lineHeight: 1.55,
                    wordBreak: 'break-word',
                  }}>
                    {m.content}
                  </div>
                  {m.link && (
                    <button
                      onClick={() => { navigate(m.link); setOpen(false); }}
                      style={{
                        background: '#F0EEFF', border: '1.5px solid #C4B5FD',
                        borderRadius: 9, padding: '7px 13px',
                        cursor: 'pointer', fontSize: 12, color: '#5A35FF', fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 6,
                        transition: 'all 0.15s', fontFamily: 'inherit',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#EDE9FF'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#F0EEFF'; }}
                    >
                      <span>→</span> {m.link_label || 'Voir la page'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Typing indicator */}
          {loading && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end' }}>
              <BeeAvatar />
              <div style={{
                background: '#F3F4F6',
                borderRadius: '4px 18px 18px 18px',
                padding: '12px 16px',
                display: 'flex', gap: 5, alignItems: 'center',
              }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: '50%', background: '#9CA3AF',
                    animation: `appeeBounce 1.3s ease-in-out ${i * 0.18}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div style={{
          padding: '12px 14px 14px',
          borderTop: '1px solid #F3F4F6',
          display: 'flex', gap: 8, flexShrink: 0,
          background: '#FAFAFA',
        }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={hasCredits ? "Pose ta question à Appee…" : "Crédits IA épuisés…"}
            disabled={loading || !hasCredits}
            style={{
              flex: 1,
              padding: '10px 14px',
              border: '1.5px solid #E5E7EB',
              borderRadius: 13,
              fontSize: 14,
              outline: 'none',
              transition: 'border-color 0.15s',
              fontFamily: 'inherit',
              background: '#fff',
              color: '#1F2937',
            }}
            onFocus={e => e.target.style.borderColor = '#7C3AED'}
            onBlur={e => e.target.style.borderColor = '#E5E7EB'}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading || !hasCredits}
            style={{
              width: 42, height: 42, borderRadius: 13, flexShrink: 0,
              background: input.trim() && !loading
                ? 'linear-gradient(135deg, #5A35FF, #7C3AED)'
                : '#E5E7EB',
              border: 'none',
              cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 19, color: '#fff',
              transition: 'all 0.18s',
              transform: 'scale(1)',
            }}
            onMouseEnter={e => { if (input.trim() && !loading) e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {loading ? '⏳' : '↑'}
          </button>
        </div>
      </div>

      {/* ── Floating button ───────────────────────────────────────── */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Ouvrir Appee"
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1001,
          width: 66, height: 66, borderRadius: '50%',
          background: open
            ? 'linear-gradient(135deg, #6428F4, #C93DB0, #FF7A1A)'
            : 'linear-gradient(135deg, #6428F4, #C93DB0, #FF7A1A)',
          border: 'none',
          padding: 3,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 24px rgba(100,40,244,0.40)',
          transition: 'all 0.28s cubic-bezier(0.34, 1.46, 0.64, 1)',
          transform: 'scale(1)',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <span style={{
          width: '100%', height: '100%', borderRadius: '50%',
          background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: open ? 20 : 14,
          color: '#7C3AED',
        }}>
          {open ? '✕' : (
            <img src={mascotImg} alt="Appee" style={{ width: 44, height: 44, objectFit: 'contain' }} />
          )}
        </span>
        {!open && (
          <span style={{
            position: 'absolute', top: 1, right: 1,
            width: 14, height: 14, borderRadius: '50%',
            background: hasCredits ? '#22C55E' : '#EF4444',
            border: '2.5px solid #fff',
            animation: hasCredits ? 'none' : 'appeePulse 2s ease-in-out infinite',
          }} />
        )}
      </button>

      {/* CSS animations */}
      <style>{`
        @keyframes appeeBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
          30% { transform: translateY(-7px); opacity: 1; }
        }
        @keyframes appeePulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.7; }
        }
      `}</style>
    </>
  );
}

/* ── Subcomponents ─────────────────────────────────────────────────────── */

function BeeAvatar() {
  return (
    <div style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, #5A35FF, #7C3AED)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(90,53,255,0.25)', overflow: 'hidden',
    }}>
      <img src={mascotImg} alt="Appee" style={{ width: 28, height: 28, objectFit: 'contain' }} />
    </div>
  );
}

function AppeeMessage({ children }) {
  return (
    <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end' }}>
      <BeeAvatar />
      <div style={{
        background: '#F3F4F6',
        borderRadius: '4px 18px 18px 18px',
        padding: '10px 14px',
        maxWidth: '82%',
      }}>
        {children}
      </div>
    </div>
  );
}
